import { Route, MousePointerClick, Users } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const SERVICES = [
  {
    num: "01",
    icon: Route,
    title: "AI Exception Architecture",
    tools: ["Lean Six Sigma", "Business Analysis"],
    value:
      "We map your new AI-driven processes to identify statistical points of failure. Instead of waiting for the system to break, we proactively design the exact escalation points and operational workflows required for seamless human intervention.",
    testid: "service-card-ai-exception-architecture",
  },
  {
    num: "02",
    icon: MousePointerClick,
    title: "Seamless Digital Adoption & In-App Guidance",
    tools: ["WalkMe Builder", "Instructional Design"],
    value:
      "When an AI flags an exception, your employees need to know exactly how to resolve it instantly. We build targeted in-app guidance directly into the flow of work. Your team is equipped to resolve the exception without leaving their screen, ensuring zero user confusion.",
    testid: "service-card-digital-adoption",
  },
  {
    num: "03",
    icon: Users,
    title: "Behavioral Alignment & Team Integration",
    tools: ["MBTI", "FIRO", "Human Performance Improvement (HPI)"],
    value:
      "Transitioning a team from \u201cdoing the work\u201d to \u201cmanaging the AI's exceptions\u201d changes the fundamental nature of their jobs. We use objective behavioral diagnostics to help leaders realign their teams, matching cognitive styles to the demands of fast-paced exception resolution and eliminating cross-departmental friction.",
    testid: "service-card-behavioral-alignment",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-hitl-section"
      className="relative border-t border-[#E2E8F0] bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <SectionHeader
          index="03"
          label="Our Services"
          testid="services-heading"
          title={
            <>
              A multidisciplinary toolkit for{" "}
              <span className="italic text-[#EB5E28]">AI rollouts</span>.
            </>
          }
          intro="We don't build the AI. We build the operational architecture around it to ensure it succeeds."
        />

        <div className="mt-16 space-y-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.testid} delay={0.06 * i}>
                <div
                  data-testid={s.testid}
                  className="group relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 shadow-sm transition-all duration-300 hover:bg-white hover:shadow-md lg:p-10"
                >
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
                    <div className="flex items-start gap-5 lg:col-span-5">
                      <span className="font-mono text-sm text-[#EB5E28]">{s.num}</span>
                      <div>
                        <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-[#E2E8F0] bg-white text-[#EB5E28]">
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <h3 className="font-display text-2xl font-medium tracking-tight text-[#0F141A] lg:text-[1.65rem]">
                          {s.title}
                        </h3>
                      </div>
                    </div>
                    <div className="lg:col-span-3">
                      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#008B9B]">
                        The Tools
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {s.tools.map((t) => (
                          <span
                            key={t}
                            className="rounded-full border border-[#E2E8F0] bg-white px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#4A5361]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="lg:col-span-4">
                      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8B95A5]">
                        The Value
                      </p>
                      <p className="text-sm leading-relaxed text-[#4A5361] lg:text-[0.95rem]">
                        {s.value}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
